import React, { useEffect, useRef, useState } from "react";
import { useChats } from "../../state/chats";
import { useAssistants } from "../../state/assistants";
import {
  Flex,
  HStack,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";

import ChatInput, { InputHeightProvider } from "./ChatInput";
import ChatMessages from "./Messages/ChatMessages";
import { useNavigate } from "react-router-dom";
import AppPage from "../../components/AppFrame/AppPage";
import AppPageHeader from "../AppFrame/AppPageHeader";
import ExistingChatAssistantButton from "./ExistingChatAssistantButton";
import EmptyChat from "./EmptyChat";
import FileUpload from "./FileUpload";
import ScrollDownButton from "./ScrollDownButton";
import { ChatFile } from "../../types";
import { DEFAULT_ASSISTANT_ID } from "../../config";
import ToastMessage from "../ToastMessage";
import SharedChat from "./SharedChat";
import { Message, Run } from "../../gen-ts/ai/assistants/v0/assistant_pb";



type ChatUIProps = {
  threadId?: string;
  isShared?: boolean;
  continueSharedChat?: boolean;
  overrideAssistantId?: string;
}

export interface OnSendMessageArgs {
  message: string;
  files: ChatFile[];
  messageHistory?: Message[]
}

const ChatUI: React.FC<ChatUIProps> = ({ threadId, isShared, continueSharedChat, overrideAssistantId }) => {

  const firstRenderRef = useRef(true);
  const navigate = useNavigate();
  const toast = useToast();

  const chatsState = useChats();
  const assistantsState = useAssistants();

  const chat = threadId ? chatsState.chats[ threadId! ] : null;

  const assistantId = overrideAssistantId ?? chat?.assistantId ?? assistantsState.selectedAssistant;
  const assistant = assistantsState.assistants[ assistantId ];

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const [ isUserScrolled, setIsUserScrolled ] = useState(false);

  const topbarBorderColor = useColorModeValue('gray.200', 'gray.700');

  let run: Run | null = null;
  if (chat?.streamingResponse?.runId) {
    run = chatsState.runs[ chat.streamingResponse.runId ];
  }


  useEffect(() => {
    if (threadId) {
      chatsState.loadChatByThreadId(threadId, false);
    }
    // eslint-disable-next-line
  }, [ threadId ]);

  useEffect(() => {
    assistantsState.loadById({
      assistantId,
      fallbackToDefault: true,
      onError: () => {
        assistantsState.setSelectedAssistantId(DEFAULT_ASSISTANT_ID);
        toast({
          position: 'top-right',
          render: (props) => {
            return (
              <ToastMessage
                message="The Assistant for this chat not found, falling back to default assistant."
                status="error"
                isCLosable={props.isClosable}
                onClose={props.onClose}
              />
            );
          },
          duration: 5000,
          isClosable: true,
        });
      },
    });
    //eslint-disable-next-line
  }, [ assistantId ]);

  useEffect(() => {
    if (chat?.messages.length === 0) {
      return;
    }


    if (!isShared || continueSharedChat) {
      scrollToBottom();
    }
    //eslint-disable-next-line
  }, [ chat, chatsState.fileLoading, run ]);

  const stopStreaming = () => {
    chat?.streamingResponse?.cancel();
  }

  const onUserScrollUpdate = (scrolled: boolean) => {
    if (scrolled !== isUserScrolled) {
      setIsUserScrolled(scrolled);
    }
  }

  const scrollToBottom = () => {
    if (messagesEndRef.current && !isUserScrolled) {
      const scrollBehavior = firstRenderRef?.current ? "auto" : "smooth";
      messagesEndRef.current.scrollIntoView({ behavior: scrollBehavior });
      firstRenderRef.current = false;
    }
  };


  const onSendMessage = async ({message, files, messageHistory}: OnSendMessageArgs) => {
    console.log('onSend', message);
    if (!chat) {
      const threadId = await chatsState.newChat({ assistantId, message, files });
      if (threadId) {
        chatsState.startStreaming({ threadId });
        navigate(`/chat/${threadId}`);
      }
    }
    else if (chat && messageHistory) {
      const threadId = await chatsState.newChat({ assistantId, message, files, messageHistory });
      if (threadId) {
        chatsState.startStreaming({ threadId });
        navigate(`/chat/${threadId}`);
      }
    }
    else {
      await chatsState.newMessage({ threadId: chat.threadId, message, assistantId, files });
      chatsState.startStreaming({ threadId: chat.threadId });
    }
  }

  if (isShared && threadId) {
    return (
      <FileUpload>
        <InputHeightProvider>
          <SharedChat
            threadId={threadId}
            onSendMessage={onSendMessage}
            messagesRef={messagesEndRef}
            continueChat={continueSharedChat ?? false}
          />
        </InputHeightProvider>
      </FileUpload>
    )
  }

  if (!chat && !chatsState.loadingById) {
    return (
      <FileUpload>
        <InputHeightProvider>
          <EmptyChat assistant={assistant} assistantId={assistantId} onSendMessage={onSendMessage} />
        </InputHeightProvider>
      </FileUpload>
    );
  }





  return (
    <AppPage
      px={4}
      py={2}
    >
      <InputHeightProvider>
        <FileUpload>
          <AppPageHeader
            onlyMobile
            actions={(
              <HStack
                width="calc(100vw - 66px)"
                justify="space-between"
                spacing={0}
              >
                <ExistingChatAssistantButton
                  isLoading={assistantsState.loadingById[ assistantId ] || chatsState.loadingById}
                  assistant={assistant}
                  isSmall
                />
              </HStack>
            )}
          />

          <VStack
            id="chat-ui-vstack"
            width="100%"
            height="100%"
            flexGrow={1}
            align="center"
            justify="stretch"
          >

            <HStack
              borderBottomWidth="1px"
              borderBottomColor={topbarBorderColor}
              width="100%"
              pb={1}
              justify="space-between"
              height={53}
              display={{ base: 'none', lg: 'flex' }}
            >
              <ExistingChatAssistantButton
                isLoading={assistantsState.loadingById[ assistantId ] || chatsState.loadingById}
                assistant={assistant}
                isSmall={false}
              />
            </HStack>
            <ChatMessages chat={chat} ref={messagesEndRef} onUserScrollUpdate={onUserScrollUpdate} />

            <Flex width="100%" justify="center">
              <ChatInput
                onSend={onSendMessage}
                onStop={stopStreaming}
                status={(chat?.streamingResponse) ? 'sending' : 'idle'}
                loading={assistantsState.loadingById[ assistantId ] || chatsState.loadingById}
                streamStartPending={chat?.streamStartPending}
                newChatPending={chatsState.newChatPending}
              />
            </Flex>
          </VStack>
          <ScrollDownButton
            visible={isUserScrolled && !Boolean(chat?.streamingResponse)}
            scrollDownRef={messagesEndRef}
          />

        </FileUpload>
      </InputHeightProvider>
    </AppPage>
  )
};

export default ChatUI;